import { Section } from '@all-hands-hq/plex-graphql-schema/dist/src/slate-utils/documentOutline';
import { Checkbox, createStyles, makeStyles, Tooltip } from '@material-ui/core';
import { SvgIconComponent } from '@material-ui/icons';
import BoardIcon from '@material-ui/icons/Assignment';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import QuestionIcon from '@material-ui/icons/LiveHelp';
import { TreeItem, TreeView } from '@material-ui/lab';
import React, { useCallback, useMemo } from 'react';
import { Node } from 'slate';

type Props = {
  document: Section;
  selectedSections: Section[];
  faqBoardSections?: Set<Section>;
  faqSections?: Set<Section>;
  onUpdateSelection: (sections: Section[]) => void;
  onSelectionRowClick: (section: Section) => void;
  onSelectionCheckboxClick: (section: Section) => void;
};

function sectionForEachRecursive(section: Section, callback: (section: Section) => void): void {
  callback(section);
  section.children.forEach((child) => sectionForEachRecursive(child, callback));
}

export function indexSectionsByNodeId(document: Section, nodeId: (section: Section) => string): Map<string, Section> {
  const result = new Map<string, Section>();

  sectionForEachRecursive(document, (section: Section) => {
    result.set(nodeId(section), section);
  });

  return result;
}

const sectionNodeId = (section: Section): string => section.startIndex.toString();

export function GoogleOutlinePreview({
  document,
  selectedSections,
  faqBoardSections,
  faqSections,
  onUpdateSelection,
  onSelectionRowClick,
  onSelectionCheckboxClick,
}: Props): React.ReactElement {
  const classes = useStyles();
  const sectionsById = useMemo(() => indexSectionsByNodeId(document, sectionNodeId), [document]);
  const sectionIds = useMemo(() => [...sectionsById.keys()], [sectionsById]);

  const renderTree = useCallback(
    (section: Section): React.ReactElement => {
      const nodeId = sectionNodeId(section);

      const checked = selectedSections.includes(section);

      return (
        <TreeItem
          key={nodeId}
          nodeId={nodeId}
          onLabelClick={(event): void => {
            // Prevent the checkbox-clicked event from firing the collapse/expand action on the tree
            event.preventDefault();
          }}
          label={
            <div
              className={classes.treeItemLabel}
              onClick={(event): void => {
                // For complex interactions (CMD + click / SHIFT + click), let default TreeView handling
                // otherwise handle normal clicks on checkbox similar to other Plex multi-selects
                if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                  event.stopPropagation();
                  onSelectionRowClick(section);
                }
              }}
            >
              <Checkbox
                checked={checked}
                color="primary"
                indeterminate={
                  !checked && selectedSections.some((selectedSection) => Section.isAncestor(section, selectedSection))
                }
                onClick={(event): void => {
                  // For complex interactions (CMD + click / SHIFT + click), let default TreeView handling
                  // otherwise handle normal clicks on checkbox similar to other Plex multi-selects
                  if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                    event.stopPropagation();
                    onSelectionCheckboxClick(section);
                  }
                }}
              />
              <span className={classes.treeItemLabelText}>{Node.string((section.title as unknown) as Node)}</span>
              {faqBoardSections?.has(section) && (
                <SectionStatus tooltipTitle="This will be imported as an FAQ Board" SvgIcon={BoardIcon} />
              )}
              {faqSections?.has(section) && (
                <SectionStatus tooltipTitle="This will be imported as an FAQ" SvgIcon={QuestionIcon} />
              )}
            </div>
          }
        >
          {section.children.map((child) => renderTree(child))}
        </TreeItem>
      );
    },
    [
      classes.treeItemLabel,
      classes.treeItemLabelText,
      faqBoardSections,
      faqSections,
      onSelectionCheckboxClick,
      onSelectionRowClick,
      selectedSections,
    ]
  );

  const onNodeSelect = useCallback(
    (event: unknown, nodeIds: string[]): void => {
      const selectedSections = nodeIds.map((nodeId) => {
        const section = sectionsById.get(nodeId);
        if (section == null) {
          throw new Error(`Invalid nodeId "${nodeId}"`);
        }
        return section;
      });
      onUpdateSelection(selectedSections);
    },
    [onUpdateSelection, sectionsById]
  );

  return (
    <TreeView
      multiSelect
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
      defaultExpanded={sectionIds}
      selected={selectedSections.map(sectionNodeId)}
      onNodeSelect={onNodeSelect}
    >
      {renderTree(document)}
    </TreeView>
  );
}

const SectionStatus = ({
  tooltipTitle,
  SvgIcon,
}: {
  tooltipTitle: string;
  SvgIcon: SvgIconComponent;
}): React.ReactElement => {
  return (
    <Tooltip title={tooltipTitle}>
      <SvgIcon fontSize="small" />
    </Tooltip>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    treeItemLabel: {
      display: 'flex',
      alignItems: 'center',
    },
    treeItemLabelText: {
      textAlign: 'left',
    },
  })
);
